// Hologram glyphs for units, buildings and missiles in flight (docs/ART-DIRECTION.md §1, §2, §5): one InstancedMesh
// per content key sharing one hologram material (per-instance faction tint + flicker phase), a small owner-shaped
// rim ring at the foot of every unit and building (solid = yours, dashed = enemy, ART-DIRECTION §2.1; it stays
// inside the glyph's footprint so it never hides the unit), a health bar over every unit, and drop lines under air
// units. Everything is on the emissive layer. Rebuilt every frame by the mirror's view systems:
// beginFrame() → add*()… → endFrame(); nothing allocates per frame.
//
// Draw calls: one per glyph type with live instances (≤ 19), rims, health bars, drop lines.

import {
  AdditiveBlending,
  DoubleSide,
  DynamicDrawUsage,
  Group,
  InstancedBufferAttribute,
  InstancedMesh,
  RingGeometry,
  ShaderMaterial,
  Vector3,
  type BufferGeometry,
  type Color,
} from 'three'
import { BUILDING_KEYS, CAPTURE, LIMITS, STRIKES, STRIKE_KEYS, UNITS, UNIT_KEYS, type Domain, type StrikeKey } from '@taxwar/content'
import { InvariantViolation, type OwnerValue, type PlayerId } from '@taxwar/net'
import type { WarCapacity } from '../../match/warSetup'
import { DEG_TO_RAD, GLOBE_ALTITUDE, latLonToVector3 } from '../geo/globeSpace'
import { flightLift, strikeArcAt, type StrikeArcOut } from '../geo/strikeArc'
import { EMISSIVE_LAYER, assignLayer } from '../layers'
import { factionRimDashes, factionRole, type Palette } from '../palette'
import { cityArtWorldPerPx } from '../network/cityArt'
import { BUILDING_GLYPHS, STRIKE_GLYPHS, UNIT_GLYPHS, type GlyphDef } from './glyphShapes'
import { GLYPH_RIM_FRAGMENT, GLYPH_RIM_VERTEX, HOLO_FRAGMENT, HOLO_VERTEX } from './glyphShaders'
import { DOMAIN_HOVER, glyphWorldSize } from './glyphSizing'
import { HealthBars } from './HealthBars'
import { LineBatch, uploadPrefix } from './LineBatch'

interface DomainStyle {
  /** Hover above the network altitude, in glyph sizes (glyphSizing.ts DOMAIN_HOVER). */
  readonly hover: number
  /** Air units get a drop line to the surface. */
  readonly dropLine: boolean
}

const DOMAIN_STYLES: Readonly<Record<Domain, DomainStyle>> = {
  land: { hover: DOMAIN_HOVER.land, dropLine: false },
  sea: { hover: DOMAIN_HOVER.sea, dropLine: false },
  air: { hover: DOMAIN_HOVER.air, dropLine: true },
}

const BUILDING_HOVER = 0.2
/** One evenly spaced ring slot per building a city can hold. */
export const BUILDING_RING_SLOTS = Math.max(CAPTURE.citySlots, CAPTURE.capitalSlots)
/** Multi-body units stand in a ring of this radius (in glyph sizes). */
const BODY_SPREAD = 0.7
/** Rim rings sit at the glyph's foot, inside its footprint (user 2026-09-27: they used to swamp the units). */
const RIM_RADIUS = 0.42
const CLUSTER_RIM_RADIUS = BODY_SPREAD + 0.3
const RIM_INNER_FRACTION = 0.86
const RIM_THETA_SEGMENTS = 32
const RIM_INTENSITY = 0.55
/** Health bars float this far above a unit's glyph base, and are this wide and tall (in glyph sizes). */
const BAR_LIFT = 1.45
const BAR_WIDTH = 1.1
const CLUSTER_BAR_WIDTH = 2 * BODY_SPREAD + 0.8
const BAR_HEIGHT = 0.14
const RIM_DASH_DUTY = 0.5
const DROP_LINE_INTENSITY = 0.35

const HOLO_FILL = 0.25
const HOLO_RIM = 1.3
const HOLO_RIM_POWER = 2
const SCAN_PERIOD_PX = 4
/** Scanline scroll: speed / period = 1.5 Hz per pixel, and the flicker is a 1.5 Hz sine (ART-DIRECTION §4: < 3 Hz). */
const SCAN_SPEED_PX_PER_SEC = 6
const SCAN_DEPTH = 0.3
const FLICKER_HZ = 1.5
const FLICKER_DEPTH = 0.12
/** Holds whole scanline and flicker cycles (60 s × 1.5 Hz), so wrapping the shader clock is seamless. */
const SHADER_TIME_WRAP_SEC = 60

const MATRIX_ELEMENTS = 16
const COLOUR_COMPONENTS = 3
const FULL_TURN = Math.PI * 2
const MIN_TANGENT_LENGTH = 1e-6

function salvosOf(key: StrikeKey): number {
  const effect = STRIKES[key].effect
  return effect.kind === 'missile' ? effect.salvos : 0
}

const MAX_SALVOS = Math.max(...STRIKE_KEYS.map(salvosOf))
const STRIKE_INSTANCES = LIMITS.maxStrikesInFlight * MAX_SALVOS

class GlyphBatch {
  readonly mesh: InstancedMesh
  private readonly colours: InstancedBufferAttribute
  private readonly phases: InstancedBufferAttribute
  count = 0

  constructor(
    geometry: BufferGeometry,
    material: ShaderMaterial,
    private readonly capacity: number,
  ) {
    this.colours = new InstancedBufferAttribute(new Float32Array(capacity * COLOUR_COMPONENTS), COLOUR_COMPONENTS).setUsage(DynamicDrawUsage)
    this.phases = new InstancedBufferAttribute(new Float32Array(capacity), 1).setUsage(DynamicDrawUsage)
    geometry.setAttribute('aPhase', this.phases)
    this.mesh = new InstancedMesh(geometry, material, capacity)
    this.mesh.instanceMatrix.setUsage(DynamicDrawUsage)
    this.mesh.instanceColor = this.colours
    this.mesh.count = 0
    // Instances move every frame; a cached bounding sphere would cull them wrongly.
    this.mesh.frustumCulled = false
    assignLayer(this.mesh, EMISSIVE_LAYER)
  }

  get matrices(): Float32Array {
    return this.mesh.instanceMatrix.array as Float32Array
  }

  /** Claims the next instance slot and paints it. */
  claim(colour: Color, phase: number): number {
    if (this.count >= this.capacity) throw new InvariantViolation(`glyph batch over its capacity of ${this.capacity}`)
    const slot = this.count++
    this.colours.setXYZ(slot, colour.r, colour.g, colour.b)
    this.phases.setX(slot, phase)
    return slot
  }

  finish(): void {
    this.mesh.count = this.count
    if (this.count === 0) return
    uploadPrefix(this.mesh.instanceMatrix, this.count * MATRIX_ELEMENTS)
    uploadPrefix(this.colours, this.count * COLOUR_COMPONENTS)
    uploadPrefix(this.phases, this.count)
  }

  dispose(): void {
    this.mesh.geometry.dispose()
    this.mesh.dispose()
  }
}

/** Writes a column-major instance matrix with basis (x, y, z) × scale at `at`. */
function writeMatrix(target: Float32Array, slot: number, x: Vector3, y: Vector3, z: Vector3, scale: number, at: Vector3): void {
  const o = slot * MATRIX_ELEMENTS
  target[o] = x.x * scale
  target[o + 1] = x.y * scale
  target[o + 2] = x.z * scale
  target[o + 3] = 0
  target[o + 4] = y.x * scale
  target[o + 5] = y.y * scale
  target[o + 6] = y.z * scale
  target[o + 7] = 0
  target[o + 8] = z.x * scale
  target[o + 9] = z.y * scale
  target[o + 10] = z.z * scale
  target[o + 11] = 0
  target[o + 12] = at.x
  target[o + 13] = at.y
  target[o + 14] = at.z
  target[o + 15] = 1
}

/** One glyph type: its instances and its size relative to a size-1 glyph. */
interface GlyphType {
  readonly batch: GlyphBatch
  readonly size: number
}

interface UnitGlyphType extends GlyphType {
  readonly domain: DomainStyle
}

function glyphType(def: GlyphDef, material: ShaderMaterial, capacity: number): GlyphType {
  return { batch: new GlyphBatch(def.build(), material, capacity), size: def.size }
}

export class GlyphLayer {
  readonly root = new Group()

  private readonly material: ShaderMaterial
  private readonly time = { value: 0 }
  private readonly motion = { value: 1 }
  /** Indexed by UnitTypeId / BuildingTypeId / StrikeTypeId (content key order). */
  private readonly unitTypes: readonly UnitGlyphType[]
  private readonly buildingTypes: readonly GlyphType[]
  /** Null for strikes that never fly. */
  private readonly strikeTypes: readonly (GlyphType | null)[]
  private readonly allBatches: readonly GlyphBatch[]
  private readonly rimGeometry = new RingGeometry(RIM_INNER_FRACTION, 1, RIM_THETA_SEGMENTS)
  private readonly rimMaterial: ShaderMaterial
  private readonly rims: GlyphBatch
  private readonly rimDashes: InstancedBufferAttribute
  private readonly dropLines: LineBatch
  private readonly healthBars: HealthBars
  /** Per CityIndex: the city art's cap in render units per CSS px (cityArt.ts), which places its building ring. */
  /** Per CityIndex: the city art's cap in render units per CSS px (cityArt.ts); with the ring radius, it places buildings. */
  private cityArtCaps: Float32Array
  private buildingRingPx: number

  private palette: Palette
  private worldPerCssPixel = 0

  // Per-frame scratch.
  private readonly up = new Vector3()
  private readonly north = new Vector3()
  private readonly east = new Vector3()
  private readonly forward = new Vector3()
  private readonly side = new Vector3()
  private readonly at = new Vector3()
  private readonly offset = new Vector3()
  private readonly ground = new Vector3()
  private readonly origin = new Vector3()
  private readonly target = new Vector3()
  private readonly arc: StrikeArcOut = { flown: 0, left: 0, progress: 0 }

  /**
   * `capacity` sizes every batch for the war; `buildingRingPx` and `cityArtCaps` (cityArt.ts) size each city's building
   * ring.
   */
  constructor(
    private readonly viewer: PlayerId,
    capacity: WarCapacity,
    buildingRingPx: number,
    cityArtCaps: Float32Array,
    palette: Palette,
    reduceEffects: boolean,
  ) {
    this.palette = palette
    this.buildingRingPx = buildingRingPx
    this.cityArtCaps = cityArtCaps
    this.dropLines = new LineBatch(capacity.units, DROP_LINE_INTENSITY)
    // One bar per unit entity.
    this.healthBars = new HealthBars(capacity.units)
    this.material = new ShaderMaterial({
      uniforms: {
        uTime: this.time,
        uMotion: this.motion,
        uFill: { value: HOLO_FILL },
        uRim: { value: HOLO_RIM },
        uRimPower: { value: HOLO_RIM_POWER },
        uScanPeriodPx: { value: SCAN_PERIOD_PX },
        uScanSpeedPx: { value: SCAN_SPEED_PX_PER_SEC },
        uScanDepth: { value: SCAN_DEPTH },
        uFlickerHz: { value: FLICKER_HZ },
        uFlickerDepth: { value: FLICKER_DEPTH },
      },
      vertexShader: HOLO_VERTEX,
      fragmentShader: HOLO_FRAGMENT,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
    })
    this.setReduceEffects(reduceEffects)
    this.unitTypes = UNIT_KEYS.map((key) => ({
      ...glyphType(UNIT_GLYPHS[key], this.material, capacity.units),
      domain: DOMAIN_STYLES[UNITS[key].domain],
    }))
    this.buildingTypes = BUILDING_KEYS.map((key) => glyphType(BUILDING_GLYPHS[key], this.material, capacity.buildings))
    this.strikeTypes = STRIKE_KEYS.map((key) => {
      const glyph = STRIKE_GLYPHS[key]
      return glyph.kind === 'missile' ? glyphType(glyph.glyph, this.material, STRIKE_INSTANCES) : null
    })

    this.rimMaterial = new ShaderMaterial({
      uniforms: { uIntensity: { value: RIM_INTENSITY }, uDashDuty: { value: RIM_DASH_DUTY } },
      vertexShader: GLYPH_RIM_VERTEX,
      fragmentShader: GLYPH_RIM_FRAGMENT,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
      side: DoubleSide,
    })
    // One rim per unit entity (each has at least one body) and per building.
    const rimInstances = capacity.units + capacity.buildings
    this.rimDashes = new InstancedBufferAttribute(new Float32Array(rimInstances), 1).setUsage(DynamicDrawUsage)
    this.rimGeometry.setAttribute('aRimDashes', this.rimDashes)
    this.rims = new GlyphBatch(this.rimGeometry, this.rimMaterial, rimInstances)

    const batches: GlyphBatch[] = [...this.unitTypes, ...this.buildingTypes].map((type) => type.batch)
    for (const type of this.strikeTypes) if (type !== null) batches.push(type.batch)
    this.allBatches = batches
    // All parts are additive and depth-read-only, so their draw order does not change the composite.
    this.root.add(this.rims.mesh, this.healthBars.mesh, this.dropLines.lines, ...batches.map((batch) => batch.mesh))
  }

  /**
   * Starts a frame. `worldPerCssPixel` (render units per CSS pixel at the surface under the camera) sizes every glyph
   * and its hover/spread in screen pixels.
   */
  beginFrame(worldPerCssPixel: number): void {
    this.worldPerCssPixel = worldPerCssPixel
    for (let i = 0; i < this.allBatches.length; i++) (this.allBatches[i] as GlyphBatch).count = 0
    this.rims.count = 0
    this.healthBars.begin()
    this.dropLines.begin()
  }

  /** World size of a glyph of `relative` size this frame. */
  private glyphSize(relative: number): number {
    return glyphWorldSize(relative, this.worldPerCssPixel)
  }

  /**
   * A unit: `bodies` glyphs in a ring (one at the centre for single-body units), a rim, a health bar filled to
   * `hpFraction`, and a drop line if airborne.
   */
  addUnit(typeId: number, owner: OwnerValue, latDeg: number, lonDeg: number, heading: number, bodies: number, hpFraction: number, phase: number): void {
    const type = this.unitTypes[typeId]
    if (type === undefined) throw new InvariantViolation(`unknown unit type id ${typeId}`)
    const batch = type.batch
    const size = this.glyphSize(type.size)
    const style = type.domain
    const colour = this.colourOf(owner)
    this.surfaceFrame(latDeg, lonDeg, heading)
    const altitude = GLOBE_ALTITUDE.network + style.hover * size
    const spread = bodies > 1 ? BODY_SPREAD * size : 0
    for (let body = 0; body < bodies; body++) {
      const angle = (FULL_TURN * body) / bodies
      this.offset.copy(this.side).multiplyScalar(Math.cos(angle) * spread).addScaledVector(this.forward, Math.sin(angle) * spread)
      this.at.copy(this.up).multiplyScalar(altitude).add(this.offset)
      writeMatrix(batch.matrices, batch.claim(colour, phase), this.side, this.up, this.forward, size, this.at)
    }
    this.ground.copy(this.up).multiplyScalar(GLOBE_ALTITUDE.network)
    this.addRim(owner, colour, this.ground, (bodies > 1 ? CLUSTER_RIM_RADIUS : RIM_RADIUS) * size)
    this.at.copy(this.up).multiplyScalar(altitude + BAR_LIFT * size)
    this.healthBars.add(this.at, (bodies > 1 ? CLUSTER_BAR_WIDTH : BAR_WIDTH) * size, BAR_HEIGHT * size, hpFraction, colour)
    if (style.dropLine) {
      this.at.copy(this.up).multiplyScalar(altitude)
      this.dropLines.push(this.ground, this.at, colour)
    }
  }

  /** A building outside its city's status arcs; its `ringSlot` spreads it around the ring (cityArt.ts sizing). */
  addBuilding(typeId: number, owner: OwnerValue, latDeg: number, lonDeg: number, cityIndex: number, ringSlot: number, phase: number): void {
    const type = this.buildingTypes[typeId]
    if (type === undefined) throw new InvariantViolation(`unknown building type id ${typeId}`)
    if (ringSlot >= BUILDING_RING_SLOTS) throw new InvariantViolation(`building ring slot ${ringSlot} beyond a city's ${BUILDING_RING_SLOTS} slots`)
    const batch = type.batch
    const size = this.glyphSize(type.size)
    const colour = this.colourOf(owner)
    this.surfaceFrame(latDeg, lonDeg, 0)
    const angle = (FULL_TURN * ringSlot) / BUILDING_RING_SLOTS
    const radius = this.buildingRingPx * cityArtWorldPerPx(this.worldPerCssPixel, this.cityArtCaps[cityIndex] as number)
    this.offset.copy(this.side).multiplyScalar(Math.cos(angle) * radius).addScaledVector(this.forward, Math.sin(angle) * radius)
    this.ground.copy(this.up).multiplyScalar(GLOBE_ALTITUDE.network).add(this.offset)
    this.at.copy(this.up).multiplyScalar(GLOBE_ALTITUDE.network + BUILDING_HOVER * size).add(this.offset)
    writeMatrix(batch.matrices, batch.claim(colour, phase), this.side, this.up, this.forward, size, this.at)
    this.addRim(owner, colour, this.ground, RIM_RADIUS * size)
  }

  /** A missile at its render position on a raised arc from origin to target, nose toward the target. */
  addStrike(
    typeId: number,
    owner: OwnerValue,
    latDeg: number,
    lonDeg: number,
    originLatDeg: number,
    originLonDeg: number,
    targetLatDeg: number,
    targetLonDeg: number,
    phase: number,
  ): void {
    const type = this.strikeTypes[typeId]
    if (type === undefined) throw new InvariantViolation(`unknown strike type id ${typeId}`)
    if (type === null) throw new InvariantViolation(`strike type ${typeId} does not fly but has a strike entity`)
    const batch = type.batch
    const size = this.glyphSize(type.size)
    this.surfaceFrame(latDeg, lonDeg, 0)
    latLonToVector3(originLatDeg, originLonDeg, 1, this.origin)
    latLonToVector3(targetLatDeg, targetLonDeg, 1, this.target)
    // Nose along the great circle toward the target (the tangent part of target − here).
    this.offset.copy(this.target).addScaledVector(this.up, -this.up.dot(this.target))
    if (this.offset.length() > MIN_TANGENT_LENGTH) {
      this.forward.copy(this.offset).normalize()
      this.side.crossVectors(this.up, this.forward)
    }
    // On the arc its ribbon draws (strikeArc.ts): the whole flight is the part flown plus the part left to its target now.
    const arc = strikeArcAt(this.origin, this.up, this.target, this.arc)
    const altitude = GLOBE_ALTITUDE.network + flightLift(arc.flown + arc.left, arc.progress) + size
    this.at.copy(this.up).multiplyScalar(altitude)
    writeMatrix(batch.matrices, batch.claim(this.colourOf(owner), phase), this.side, this.up, this.forward, size, this.at)
  }

  endFrame(): void {
    for (let i = 0; i < this.allBatches.length; i++) (this.allBatches[i] as GlyphBatch).finish()
    this.rims.finish()
    if (this.rims.count > 0) uploadPrefix(this.rimDashes, this.rims.count)
    this.healthBars.end()
    this.dropLines.end()
  }

  update(dtSeconds: number): void {
    this.time.value = (this.time.value + dtSeconds) % SHADER_TIME_WRAP_SEC
  }

  /** Glyphs are painted every frame, so the next frame shows the new palette. */
  setPalette(palette: Palette): void {
    this.palette = palette
  }

  /** Retunes the building rings (the tuner, ROADMAP D33); buildings are placed from them on the next frame. */
  setBuildingRing(buildingRingPx: number, cityArtCaps: Float32Array): void {
    if (cityArtCaps.length !== this.cityArtCaps.length) throw new InvariantViolation(`${cityArtCaps.length} city art caps for ${this.cityArtCaps.length} cities`)
    this.buildingRingPx = buildingRingPx
    this.cityArtCaps = cityArtCaps
  }

  /** ART-DIRECTION §4: "Reduce effects" stops the scanline scroll and the flicker. */
  setReduceEffects(reduce: boolean): void {
    this.motion.value = reduce ? 0 : 1
  }

  dispose(): void {
    this.root.removeFromParent()
    this.root.clear()
    for (const batch of this.allBatches) batch.dispose()
    this.rims.dispose()
    this.healthBars.dispose()
    this.dropLines.dispose()
    this.material.dispose()
    this.rimMaterial.dispose()
  }

  private colourOf(owner: OwnerValue): Color {
    return this.palette[factionRole(owner, this.viewer)]
  }

  private addRim(owner: OwnerValue, colour: Color, at: Vector3, radius: number): void {
    const slot = this.rims.claim(colour, 0)
    this.rimDashes.setX(slot, factionRimDashes(factionRole(owner, this.viewer)))
    // Ring plane (XY) onto the tangent plane: X = forward, Y = side (up × forward), Z = up.
    writeMatrix(this.rims.matrices, slot, this.forward, this.side, this.up, radius, at)
  }

  /** Unit normal `up`, and `forward` / `side` = the heading direction and up × forward in the tangent plane. */
  private surfaceFrame(latDeg: number, lonDeg: number, heading: number): void {
    const lat = latDeg * DEG_TO_RAD
    const lon = lonDeg * DEG_TO_RAD
    const sinLat = Math.sin(lat)
    const sinLon = Math.sin(lon)
    const cosLon = Math.cos(lon)
    latLonToVector3(latDeg, lonDeg, 1, this.up)
    this.north.set(-sinLat * sinLon, Math.cos(lat), -sinLat * cosLon)
    this.east.set(cosLon, 0, -sinLon)
    this.forward.copy(this.north).multiplyScalar(Math.cos(heading)).addScaledVector(this.east, Math.sin(heading))
    this.side.crossVectors(this.up, this.forward)
  }
}
